/**
 * CampaignTzSchedulingPage — timezone-aware campaign scheduling.
 * Ported from pre-nuke legacy SchedulingPage: table of rules (Campaign, TZ, Schedule,
 * Active window, Status) + "New Schedule" drawer with campaign picker, TZ select,
 * day-of-week picker, and start/end hour controls.
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useCampaignTzSchedulingList,
	useCampaignTzSchedulingCreate,
	useCampaignTzSchedulingDelete,
} from "./hooks";
import type {
	CampaignTzScheduleRow,
	ScheduleAction,
	ScheduleScope,
} from "./api";

// ─── design tokens ────────────────────────────────────────────────
const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20";
const SELECT = INPUT + " cursor-pointer";
const BTN =
	"rounded-[6px] border border-sky-600 bg-sky-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_OUTLINE =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-200 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50";
const LABEL = "text-xs font-medium text-zinc-600";

// ─── timezone helpers ─────────────────────────────────────────────
const FALLBACK_TZS = [
	"UTC",
	"America/New_York",
	"America/Chicago",
	"America/Denver",
	"America/Los_Angeles",
	"America/Sao_Paulo",
	"Europe/London",
	"Europe/Berlin",
	"Europe/Madrid",
	"Europe/Istanbul",
	"Asia/Dubai",
	"Asia/Kolkata",
	"Asia/Singapore",
	"Asia/Tokyo",
	"Australia/Sydney",
];

function getTimezones(): string[] {
	try {
		const intl = Intl as unknown as {
			supportedValuesOf?: (k: string) => string[];
		};
		const zones = intl.supportedValuesOf?.("timeZone");
		if (Array.isArray(zones) && zones.length > 0) return zones;
	} catch {
		// fall through
	}
	return FALLBACK_TZS;
}

// ─── day-of-week mask helpers ─────────────────────────────────────
const DOW_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function maskToDays(mask: number): number[] {
	const out: number[] = [];
	for (let i = 0; i < 7; i++) if (mask & (1 << i)) out.push(i);
	return out;
}

function daysToMask(days: number[]): number {
	return days.reduce((m, d) => m | (1 << d), 0);
}

function formatHour(h: number): string {
	return `${String(h).padStart(2, "0")}:00`;
}

// ─── drawer ───────────────────────────────────────────────────────
interface DrawerProps {
	onClose: () => void;
	onSubmit: (body: {
		campaign_id: string | null;
		scope: ScheduleScope;
		scope_id: string | null;
		timezone_iana: string;
		day_of_week_mask: number;
		hour_start: number;
		hour_end: number;
		action: ScheduleAction;
		target_url: string | null;
		enabled: number;
	}) => void;
	pending: boolean;
	error: Error | null;
}

function NewScheduleDrawer({ onClose, onSubmit, pending, error }: DrawerProps) {
	const zones = useMemo(() => getTimezones(), []);
	const [campaignId, setCampaignId] = useState("");
	const [scope, setScope] = useState<ScheduleScope>("global");
	const [scopeId, setScopeId] = useState("");
	const [tz, setTz] = useState("UTC");
	const [mask, setMask] = useState(127); // all days
	const [hourStart, setHourStart] = useState(0);
	const [hourEnd, setHourEnd] = useState(24);
	const [action, setAction] = useState<ScheduleAction>("allow");
	const [targetUrl, setTargetUrl] = useState("");
	const [enabled, setEnabled] = useState(true);

	const selectedDays = maskToDays(mask);
	const toggleDay = (d: number) => {
		const next = selectedDays.includes(d)
			? selectedDays.filter((x) => x !== d)
			: [...selectedDays, d];
		setMask(daysToMask(next));
	};

	const valid =
		hourEnd > hourStart &&
		hourStart >= 0 &&
		hourEnd <= 24 &&
		mask > 0 &&
		(action !== "redirect_to" || targetUrl.trim().length > 0);

	const submit = (e: FormEvent) => {
		e.preventDefault();
		if (!valid) return;
		onSubmit({
			campaign_id: campaignId.trim() || null,
			scope,
			scope_id: scope === "global" ? null : scopeId.trim() || null,
			timezone_iana: tz,
			day_of_week_mask: mask,
			hour_start: hourStart,
			hour_end: hourEnd,
			action,
			target_url: action === "redirect_to" ? targetUrl.trim() || null : null,
			enabled: enabled ? 1 : 0,
		});
	};

	return (
		<div
			className="fixed inset-0 z-40 flex justify-end bg-zinc-900/30"
			role="dialog"
			aria-modal="true"
			aria-label="New schedule"
			data-testid="campaign-tz-scheduling-drawer"
			onClick={(e) => {
				if (e.target === e.currentTarget) onClose();
			}}
		>
			<aside className="flex h-full w-full max-w-xl flex-col overflow-y-auto border-l border-zinc-200 bg-white shadow-xl">
				<header className="flex items-center justify-between border-b border-zinc-200 px-6 py-4">
					<div className="flex flex-col">
						<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
							New Schedule
						</p>
						<h2 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-lg font-semibold text-transparent">
							Campaign timezone rule
						</h2>
					</div>
					<button
						type="button"
						onClick={onClose}
						className={BTN_GHOST}
						aria-label="Close drawer"
					>
						Close
					</button>
				</header>

				<form
					onSubmit={submit}
					className="flex flex-col gap-5 px-6 py-5"
					data-testid="campaign-tz-scheduling-drawer-form"
				>
					<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
						<label className="flex flex-col gap-1.5">
							<span className={LABEL}>Campaign ID</span>
							<input
								type="text"
								className={INPUT}
								placeholder="campaign-uuid (optional)"
								value={campaignId}
								onChange={(e) => setCampaignId(e.currentTarget.value)}
								data-testid="campaign-tz-scheduling-campaign-input"
							/>
						</label>
						<label className="flex flex-col gap-1.5">
							<span className={LABEL}>Scope</span>
							<select
								className={SELECT}
								value={scope}
								onChange={(e) => setScope(e.currentTarget.value as ScheduleScope)}
								data-testid="campaign-tz-scheduling-scope-select"
							>
								<option value="global">Global (tenant-wide)</option>
								<option value="offer">Offer</option>
								<option value="affiliate">Affiliate</option>
							</select>
						</label>
					</div>

					{scope !== "global" ? (
						<label className="flex flex-col gap-1.5">
							<span className={LABEL}>
								{scope === "offer" ? "Offer ID" : "Affiliate ID"}
							</span>
							<input
								type="text"
								className={INPUT}
								placeholder={`Enter ${scope} id`}
								value={scopeId}
								onChange={(e) => setScopeId(e.currentTarget.value)}
								data-testid="campaign-tz-scheduling-scope-id-input"
							/>
						</label>
					) : null}

					<label className="flex flex-col gap-1.5">
						<span className={LABEL}>Timezone</span>
						<select
							className={SELECT}
							value={tz}
							onChange={(e) => setTz(e.currentTarget.value)}
							data-testid="campaign-tz-scheduling-tz-select"
						>
							{zones.map((z) => (
								<option key={z} value={z}>
									{z}
								</option>
							))}
						</select>
					</label>

					<div className="flex flex-col gap-2">
						<span className={LABEL}>Days of week</span>
						<div
							className="flex flex-wrap gap-1.5"
							data-testid="campaign-tz-scheduling-dow-picker"
						>
							{DOW_LABELS.map((lbl, i) => {
								const on = selectedDays.includes(i);
								return (
									<button
										key={lbl}
										type="button"
										onClick={() => toggleDay(i)}
										className={
											"rounded-[6px] border px-3 py-1.5 text-xs font-medium transition-colors " +
											(on
												? "border-sky-600 bg-sky-600 text-white"
												: "border-zinc-300 bg-white text-zinc-600 hover:border-sky-400")
										}
										data-testid={`campaign-tz-scheduling-dow-${i}`}
										aria-pressed={on}
									>
										{lbl}
									</button>
								);
							})}
						</div>
					</div>

					<div className="grid grid-cols-2 gap-3">
						<label className="flex flex-col gap-1.5">
							<span className={LABEL}>Start hour (0-23)</span>
							<input
								type="number"
								min={0}
								max={23}
								className={INPUT}
								value={hourStart}
								onChange={(e) =>
									setHourStart(
										Math.max(0, Math.min(23, Number(e.currentTarget.value) || 0)),
									)
								}
								data-testid="campaign-tz-scheduling-hour-start"
							/>
						</label>
						<label className="flex flex-col gap-1.5">
							<span className={LABEL}>End hour (1-24)</span>
							<input
								type="number"
								min={1}
								max={24}
								className={INPUT}
								value={hourEnd}
								onChange={(e) =>
									setHourEnd(
										Math.max(1, Math.min(24, Number(e.currentTarget.value) || 1)),
									)
								}
								data-testid="campaign-tz-scheduling-hour-end"
							/>
						</label>
					</div>

					<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
						<label className="flex flex-col gap-1.5">
							<span className={LABEL}>Action</span>
							<select
								className={SELECT}
								value={action}
								onChange={(e) =>
									setAction(e.currentTarget.value as ScheduleAction)
								}
								data-testid="campaign-tz-scheduling-action-select"
							>
								<option value="allow">Allow</option>
								<option value="block">Block</option>
								<option value="redirect_to">Redirect to URL</option>
							</select>
						</label>
						{action === "redirect_to" ? (
							<label className="flex flex-col gap-1.5">
								<span className={LABEL}>Redirect URL</span>
								<input
									type="url"
									className={INPUT}
									placeholder="https://fallback.example.com"
									value={targetUrl}
									onChange={(e) => setTargetUrl(e.currentTarget.value)}
									data-testid="campaign-tz-scheduling-target-url"
								/>
							</label>
						) : null}
					</div>

					<label className="flex items-center gap-2">
						<input
							type="checkbox"
							className="h-4 w-4 rounded border-zinc-300 text-sky-600 focus:ring-sky-500/30"
							checked={enabled}
							onChange={(e) => setEnabled(e.currentTarget.checked)}
							data-testid="campaign-tz-scheduling-enabled-toggle"
						/>
						<span className="text-sm text-zinc-700">Rule enabled</span>
					</label>

					{error ? (
						<p className="text-xs text-red-600" role="alert">
							{error.message}
						</p>
					) : null}

					<footer className="mt-2 flex justify-end gap-2 border-t border-zinc-200 pt-4">
						<button
							type="button"
							onClick={onClose}
							className={BTN_OUTLINE}
							disabled={pending}
						>
							Cancel
						</button>
						<button
							type="submit"
							className={BTN}
							disabled={pending || !valid}
							data-testid="campaign-tz-scheduling-submit"
						>
							{pending ? "Saving." : "Create schedule"}
						</button>
					</footer>
				</form>
			</aside>
		</div>
	);
}

// ─── row rendering ────────────────────────────────────────────────
function renderDays(mask: number): string {
	const days = maskToDays(mask);
	if (days.length === 7) return "Every day";
	if (days.length === 0) return "No days";
	return days.map((d) => DOW_LABELS[d]).join(", ");
}

function actionChip(action: ScheduleAction): { text: string; cls: string } {
	if (action === "block")
		return {
			text: "Block",
			cls: "border-red-200 bg-red-50 text-red-700",
		};
	if (action === "redirect_to")
		return {
			text: "Redirect",
			cls: "border-sky-200 bg-sky-50 text-sky-700",
		};
	return {
		text: "Allow",
		cls: "border-emerald-200 bg-emerald-50 text-emerald-700",
	};
}

function statusChip(row: CampaignTzScheduleRow): { text: string; cls: string } {
	if (row.enabled === 1)
		return {
			text: "Active",
			cls: "border-emerald-200 bg-emerald-50 text-emerald-700",
		};
	return {
		text: "Paused",
		cls: "border-zinc-200 bg-zinc-50 text-zinc-600",
	};
}

// ─── page ─────────────────────────────────────────────────────────
export function CampaignTzSchedulingPage() {
	const listQuery = useCampaignTzSchedulingList();
	const createMut = useCampaignTzSchedulingCreate();
	const deleteMut = useCampaignTzSchedulingDelete();

	const [drawerOpen, setDrawerOpen] = useState(false);

	const items = listQuery.data?.items ?? [];

	const handleSubmit = (body: Parameters<typeof createMut.mutate>[0]) => {
		createMut.mutate(body, {
			onSuccess: () => setDrawerOpen(false),
		});
	};

	return (
		<main
			data-testid="campaign-tz-scheduling-page"
			className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
				<div className="flex flex-col gap-2">
					<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
						campaign-tz-scheduling
					</p>
					<h1 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold text-transparent">
						Campaign Timezone Scheduling
					</h1>
					<p className="max-w-2xl text-sm text-zinc-600">
						Timezone-aware day-parting rules for campaigns, offers, affiliates
						and tenant-wide traffic. Match local time in any IANA zone and
						allow, block, or redirect.
					</p>
				</div>
				<button
					type="button"
					className={BTN}
					onClick={() => setDrawerOpen(true)}
					data-testid="campaign-tz-scheduling-new-button"
				>
					New Schedule
				</button>
			</header>

			<section className={CARD} aria-label="Campaign schedules">
				<div className="flex items-center justify-between">
					<h2 className="text-base font-semibold text-zinc-900">Schedules</h2>
					<span className="text-xs text-zinc-500">
						{listQuery.data ? `${listQuery.data.count} total` : ""}
					</span>
				</div>

				{listQuery.isLoading ? (
					<p
						className="mt-4 text-sm text-zinc-500"
						data-testid="campaign-tz-scheduling-loading"
					>
						Loading.
					</p>
				) : listQuery.error ? (
					<p className="mt-4 text-sm text-red-600" role="alert">
						{listQuery.error.message}
					</p>
				) : items.length === 0 ? (
					<p
						className="mt-4 text-sm text-zinc-500"
						data-testid="campaign-tz-scheduling-empty"
					>
						No schedules yet. Create one to restrict campaign traffic to
						specific days and hours.
					</p>
				) : (
					<div
						className="mt-4 overflow-x-auto"
						data-testid="campaign-tz-scheduling-list"
					>
						<table className="w-full border-collapse text-sm">
							<thead>
								<tr className="border-b border-zinc-200 text-left text-xs uppercase tracking-wider text-zinc-500">
									<th className="py-2 pr-4 font-medium">Campaign</th>
									<th className="py-2 pr-4 font-medium">Timezone</th>
									<th className="py-2 pr-4 font-medium">Schedule</th>
									<th className="py-2 pr-4 font-medium">Active window</th>
									<th className="py-2 pr-4 font-medium">Status</th>
									<th className="py-2 pr-4 font-medium"></th>
								</tr>
							</thead>
							<tbody>
								{items.map((row) => {
									const act = actionChip(row.action);
									const st = statusChip(row);
									const campaignLbl =
										row.campaign_id ??
										(row.scope === "global"
											? "All campaigns"
											: `${row.scope}${row.scope_id ? ` #${row.scope_id.slice(-6)}` : ""}`);
									return (
										<tr
											key={row.id}
											className="border-b border-zinc-100 last:border-0"
											data-testid={`campaign-tz-scheduling-row-${row.id}`}
										>
											<td className="py-3 pr-4">
												<div className="flex flex-col">
													<span className="font-medium text-zinc-900">
														{campaignLbl}
													</span>
													<span className="font-mono text-[10px] text-zinc-400">
														{row.id}
													</span>
												</div>
											</td>
											<td className="py-3 pr-4 text-zinc-700">
												{row.timezone_iana}
											</td>
											<td className="py-3 pr-4 text-zinc-700">
												{renderDays(row.day_of_week_mask)}
											</td>
											<td className="py-3 pr-4 text-zinc-700">
												{formatHour(row.hour_start)} –{" "}
												{formatHour(row.hour_end)}
												<span className="ml-2 inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-medium border-zinc-200 bg-zinc-50 text-zinc-600">
													{act.text}
												</span>
											</td>
											<td className="py-3 pr-4">
												<span
													className={
														"inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium " +
														st.cls
													}
												>
													{st.text}
												</span>
											</td>
											<td className="py-3 pr-4 text-right">
												<button
													type="button"
													className={BTN_GHOST}
													onClick={() => deleteMut.mutate(row.id)}
													disabled={deleteMut.isPending}
													data-testid={`campaign-tz-scheduling-delete-${row.id}`}
												>
													Delete
												</button>
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}
			</section>

			{drawerOpen ? (
				<NewScheduleDrawer
					onClose={() => setDrawerOpen(false)}
					onSubmit={handleSubmit}
					pending={createMut.isPending}
					error={createMut.error ?? null}
				/>
			) : null}
		</main>
	);
}

export default CampaignTzSchedulingPage;
